/**
 * Speech Service - Abstraction layer for speech-to-text and text-to-speech
 * 
 * Uses Web Speech API for browser-based speech functionality.
 * Mobile compatibility:
 * - Chrome Android: Full support
 * - Safari iOS: TTS only (no STT - uses native dictation fallback)
 */

export interface SpeechService {
  startListening(onResult: (text: string) => void, onError: (error: string) => void, language?: string): void;
  stopListening(): void;
  speak(text: string, onEnd?: () => void): void;
  stopSpeaking(): void;
  isListening(): boolean;
  isSpeaking(): boolean;
  isSupported(): { stt: boolean; tts: boolean };
}

class WebSpeechService implements SpeechService {
  private recognition: any = null;
  private synthesis: SpeechSynthesis;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private listening: boolean = false;
  private speaking: boolean = false;
  private silenceTimer: NodeJS.Timeout | null = null;
  private finalTranscript: string = '';
  private userGender: 'male' | 'female' = 'male';
  private userLanguage: string = 'en-GB';

  constructor() {
    this.synthesis = window.speechSynthesis;
    
    // Initialize speech recognition if available
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false;
      this.recognition.interimResults = true; // Show interim results
      this.recognition.lang = 'en-US';
      this.recognition.maxAlternatives = 1;
    }
  }

  /**
   * Check if speech features are supported
   */
  isSupported(): { stt: boolean; tts: boolean } {
    return {
      stt: !!this.recognition,
      tts: 'speechSynthesis' in window,
    };
  }

  /**
   * Start listening for speech input (Speech-to-Text)
   */
  startListening(
    onResult: (text: string) => void,
    onError: (error: string) => void,
    language?: string
  ): void {
    
    if (!this.recognition) {
      onError('Speech recognition is not supported in this browser. Please use Chrome or enable dictation.');
      return;
    }

    if (this.listening) {
      return;
    }

    // Set recognition language from user preference
    this.recognition.lang = language || 'en-US';
    
    this.finalTranscript = '';
    
    this.recognition.onresult = (event: any) => {
      // Clear any existing silence timer
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          this.finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Start 1-second silence timer after any speech
      if (this.finalTranscript.trim() || interimTranscript.trim()) {
        this.silenceTimer = setTimeout(() => {
          const result = (this.finalTranscript + interimTranscript).trim();
          if (result) {
            this.listening = false; // Set to false before calling onResult
            this.finalTranscript = '';
            if (this.recognition) {
              this.recognition.stop();
            }
            onResult(result);
          }
        }, 1000);
      }
    };

    this.recognition.onerror = (event: any) => {
      let errorMessage = 'Speech recognition failed';
      
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please try again.';
          break;
        case 'audio-capture':
          errorMessage = 'Microphone not found or not accessible.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please enable microphone permissions.';
          break;
        case 'network':
          errorMessage = 'Network error occurred during speech recognition.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }
      
      onError(errorMessage);
      this.listening = false;
    };

    this.recognition.onend = () => {
      this.listening = false;
    };

    try {
      this.recognition.start();
      this.listening = true;
    } catch (error) {
      onError('Failed to start speech recognition. Please try again.');
      this.listening = false;
    }
  }

  /**
   * Stop listening for speech input
   */
  stopListening(): void {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    if (this.recognition && this.listening) {
      this.recognition.stop();
      this.listening = false;
    }
    this.finalTranscript = '';
  }

  /**
   * Strip markdown syntax from text for speech
   */
  private stripMarkdown(text: string): string {
    return text
      .replace(/\*\*(.+?)\*\*/g, '$1')  // Bold
      .replace(/\*(.+?)\*/g, '$1')      // Italic
      .replace(/_(.+?)_/g, '$1')        // Italic underscore
      .replace(/~~(.+?)~~/g, '$1')      // Strikethrough
      .replace(/`(.+?)`/g, '$1')        // Inline code
      .replace(/```[\s\S]*?```/g, '')   // Code blocks
      .replace(/#{1,6}\s/g, '')         // Headers
      .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Links
      .replace(/!\[.*?\]\(.+?\)/g, '')  // Images
      .replace(/^\s*[-*+]\s/gm, '')     // Unordered lists
      .replace(/^\s*\d+\.\s/gm, '')     // Ordered lists
      .replace(/^\s*>\s/gm, '')         // Blockquotes
      .replace(/\n{2,}/g, '. ')         // Multiple newlines to period
      .replace(/\n/g, ' ')              // Single newlines to space
      .trim();
  }



  /**
   * Speak text aloud (Text-to-Speech)
   */
  speak(text: string, onEnd?: () => void, userGender?: 'male' | 'female', userLanguage?: string): void {
    // Strip markdown before speaking
    const cleanText = this.stripMarkdown(text);

    // Prevent duplicate speech - check if we're already speaking this exact text
    if (this.speaking && this.currentUtterance?.text === cleanText) {
      return;
    }
    
    // Cancel any pending speech before starting new one
    if (this.synthesis.speaking || this.synthesis.pending) {
      this.synthesis.cancel();
    }

    // Create utterance
    this.currentUtterance = new SpeechSynthesisUtterance(cleanText);
    this.currentUtterance.rate = 1.1; // 10% faster
    this.currentUtterance.pitch = 1.0;
    this.currentUtterance.volume = 1.0;
    
    // Get available voices
    const voices = this.synthesis.getVoices();
    
    // Use user preferences (default to male, en-GB)
    const targetGender = userGender || 'male';
    const targetLang = userLanguage || 'en-GB';
    
    // Find Google voice matching language and gender
    let selectedVoice = voices.find(voice => {
      const nameLower = voice.name.toLowerCase();
      const matchesLang = voice.lang.startsWith(targetLang.split('-')[0]);
      const matchesGender = targetGender === 'male'
        ? !nameLower.includes('female') && nameLower.includes('male')
        : nameLower.includes('female');
      return nameLower.includes('google') && matchesLang && matchesGender;
    });
    
    // Fallback: any Google voice for the language
    if (!selectedVoice) {
      selectedVoice = voices.find(voice => {
        const nameLower = voice.name.toLowerCase();
        return nameLower.includes('google') && voice.lang.startsWith(targetLang.split('-')[0]);
      });
    }
    
    // Fallback: any voice for the language
    if (!selectedVoice) {
      selectedVoice = voices.find(voice => voice.lang.startsWith(targetLang.split('-')[0]));
    }
    
    if (selectedVoice) {
      this.currentUtterance.voice = selectedVoice;
      this.currentUtterance.lang = selectedVoice.lang;
    } else {
      this.currentUtterance.lang = targetLang;
    }

    this.currentUtterance.onstart = () => {
      this.speaking = true;
    };

    this.currentUtterance.onend = () => {
      this.speaking = false;
      if (onEnd) {
        onEnd();
      }
    };

    this.currentUtterance.onerror = () => {
      this.speaking = false;
    };

    // Speak
    this.synthesis.speak(this.currentUtterance);
    this.speaking = true;
  }

  /**
   * Stop speaking
   */
  stopSpeaking(): void {
    if (this.synthesis.speaking || this.synthesis.pending) {
      this.synthesis.cancel();
    }
    this.speaking = false;
    this.currentUtterance = null;
  }

  /**
   * Check if currently listening
   */
  isListening(): boolean {
    return this.listening;
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.speaking;
  }
}

// Export singleton instance
export const speechService = new WebSpeechService();
